import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannels } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomUser, MessageMention } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { SideModal, SideModalHeader } from '@connect-x-app/web-ui';

import { CrewMemberInfoItem } from '../crewMemberInfoItem';
import { CrewProfile } from '../crewProfile';
import { ExternalMentionInfoItem } from '../externalMentionInfoItem';

type MentionInfoSideModalProps = {
  activeMention?: MessageMention | null;
  onClose: () => void;
  showOfflineText?: boolean;
};

type Screen = 'MENTION_INFO' | 'MENTION_DETAILS';

export const MentionInfoSideModal: FC<MentionInfoSideModalProps> = ({ activeMention, onClose, showOfflineText }) => {
  const { selectedChannel } = useChannels();
  const { t } = useTranslation();

  const [screenToDisplay, setScreenToDisplay] = useState<Screen>('MENTION_INFO');
  const [activeUser, setActiveUser] = useState<FlightRoomUser | null>(null);

  useEffect(() => {
    if (!activeMention) {
      setScreenToDisplay('MENTION_INFO');
    }
  }, [activeMention]);

  const getUsers = () => {
    const mention = activeMention;
    return mention && selectedChannel?.users?.filter(user => mention?.users?.includes(user.externalId));
  };

  const handleUserClick = (user: FlightRoomUser) => {
    setActiveUser(user);
    setScreenToDisplay('MENTION_DETAILS');
  };

  const renderMentionInfo = () => {
    if (activeMention) {
      const mentionedUsers = getUsers();
      if (!mentionedUsers?.length) {
        return <ExternalMentionInfoItem mention={activeMention} />;
      }
      return renderInternalMention(mentionedUsers);
    }

    return undefined;
  };

  const renderInternalMention = (flightRoomUsers: FlightRoomUser[]) => {
    return (
      flightRoomUsers && (
        <>
          {flightRoomUsers?.map(crewMember => (
            <CrewMemberInfoItem crewMember={crewMember} key={crewMember.externalId} onClick={handleUserClick} />
          ))}
        </>
      )
    );
  };

  const renderMentionDetails = () => {
    return activeUser ? <CrewProfile crewMember={activeUser} /> : null;
  };

  const renderContent = () => {
    if (showOfflineText) {
      return <Text>{t('OFFLINE.USERS')}</Text>;
    }

    switch (screenToDisplay) {
      case 'MENTION_DETAILS':
        return renderMentionDetails();
      case 'MENTION_INFO':
      default:
        return renderMentionInfo();
    }
  };

  const renderHeader = (): SideModalHeader => {
    switch (screenToDisplay) {
      case 'MENTION_DETAILS':
        return {
          backgroundColor: COLORS.victor.$03,
          leftIcon: {
            iconName: 'arrowLeft',
            onClick: () => setScreenToDisplay('MENTION_INFO'),
          },
          title: { props: { uppercase: false }, text: t('PROFILE.HEADER_TITLE') },
        };
      case 'MENTION_INFO':
      default:
        return {
          rightIcon: { iconName: 'closeCross', onClick: onClose },
          title: { props: { uppercase: false }, text: activeMention?.label ? `@${activeMention.label}` : '' },
        };
    }
  };

  return (
    <SideModal
      content={renderContent()}
      fullwidth={screenToDisplay === 'MENTION_DETAILS'}
      header={renderHeader()}
      isVisible={!!activeMention}
      onBackgroundClick={onClose}
    />
  );
};
